import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  TextField,
  Typography
} from '@mui/material';
import SftGlobalData from '../../../library/SftGlobalData';

// Simple validation functions
const validateEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

const validateVAT = (vat) => {
  // This is a simplified validation. VAT formats vary by country
  return vat.length > 5;
};

const validateIBAN = (iban) => {
  // Simple IBAN validation - actual IBAN validation is more complex
  return iban.length >= 15 && iban.length <= 34;
};

const validatePhone = (phone) => {
  // Basic phone number validation
  return /^[+]?[\d\s()-]{8,20}$/.test(phone);
};

// Helper function to convert File to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }
    
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = () => {
      // The result includes the data URI scheme (e.g., "data:application/pdf;base64,")
      // We can either keep it or strip it based on your requirements
      resolve(reader.result);
    };
    
    reader.onerror = (error) => {
      reject(error);
    };
  });
};

const PeppolRegistrationForm = ({ 
  initialData = {}, 
  onSubmit, 
  onCancel, 
  t,
  isEditing = false,
  setLoading = () => {},
  disabledFields = []
}) => {
  const [formData, setFormData] = useState({
    vat: '',
    homepage: '',
    email: '',
    iban: '',
    corpname: '',
    street: '',
    streetNr: '',
    city: '',
    zip: '',
    country: '',
    phoneNumber: '',
    idDocuments: null,
  });

  // List of countries for the dropdown
  const countries = [
    {value: 'Belgium', label: t('Belgium')}, 
    {value: 'France', label: t('France')},
    {value: 'Germany', label: t('Germany')},
    {value: 'Netherlands', label: t('Netherlands')},
    {value: 'Luxembourg', label: t('Luxembourg')},
    {value: 'Sweden', label: t('Sweden')},
    {value: 'Ireland', label: t('Ireland')}
  ];

  const [errors, setErrors] = useState({});
  const [fileNames, setFileNames] = useState({
    idDocuments: '',
    businessRegistration: ''
  });
  const [consentChecked, setConsentChecked] = useState(isEditing); // Auto-check for editing

  // When initialData changes, update form fields
  useEffect(() => {
    if (initialData) {
      console.log(` initialData:`, initialData);
      // Extract street and street number from address1 if available
      let street = initialData.address1 || '';
      let streetNr = '';
      
      // Simple logic to extract street number from address
      const addressParts = street.split(' ');
      if (addressParts.length > 1) {
        // Check if last part is a number or includes a number
        const lastPart = addressParts[addressParts.length - 1];
        if (/\d/.test(lastPart)) {
          streetNr = lastPart;
          street = addressParts.slice(0, -1).join(' ');
        }
      }

      setFormData(prevState => ({
        ...prevState,
        vat: initialData.vat || '',
        corpname: initialData.name || '',
        city: initialData.city || '',
        zip: initialData.zip || '',
        country: countries.find(c => c.label.toLowerCase().replace(/[^\w\s]/gi, '') === initialData.country?.toLowerCase().replace(/[^\w\s]/gi, ''))?.value || countries.find(c => c.value == initialData.country)?.value || '',
        street,
        streetNr,
        email: initialData.email || '',
        homepage: initialData.homepage || '',
        phoneNumber: initialData.phoneNumber || '',
        iban: initialData.iban || '',
        // When editing, we don't need new ID documents
        idDocuments: isEditing ? 'preserved' : null
      }));
    }
  }, [initialData, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Skip changes for disabled fields
    if (disabledFields.includes(name)) {
      return;
    }
    
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error when user edits field - properly remove the key
    if (errors[name]) {
      setErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleConsentChange = (e) => {
    setConsentChecked(e.target.checked);
    
    // Clear consent error if it exists - properly remove the key
    if (errors.consent) {
      setErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors.consent;
        return newErrors;
      });
    }
  };


  // Helper function to compress image
  const compressImage = async (file, maxSizeMB = 0.7) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Start with original dimensions
          let width = img.width;
          let height = img.height;
          
          // Calculate initial quality
          let quality = 0.85; // Start with slightly lower quality
          
          const compress = async () => {
            // Set canvas dimensions
            canvas.width = width;
            canvas.height = height;
            
            // Draw image on canvas
            ctx.drawImage(img, 0, 0, width, height);
            
            // Convert to blob with compression
            canvas.toBlob(async (blob) => {
              const sizeInMB = blob.size / 1024 / 1024;
              
              if (sizeInMB <= maxSizeMB || (quality <= 0.1 && width < 800)) {
                // Size is acceptable or we've reached minimum quality and size
                resolve(blob);
              } else {
                // Need more compression
                if (quality > 0.3) {
                  // First try reducing quality more aggressively
                  quality -= 0.15;
                } else {
                  // If quality is low, reduce dimensions more aggressively
                  width *= 0.85;
                  height *= 0.85;
                  // Also reduce quality a bit more
                  quality = Math.max(0.1, quality - 0.05);
                }
                compress();
              }
            }, file.type, quality);
          };
          
          compress();
        };
        img.onerror = () => reject(new Error('Failed to load image'));
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
    });
  };

  // Helper function to check if file is an image
  const isImageFile = (file) => {
    const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    return imageTypes.includes(file.type.toLowerCase());
  };

  // Helper function to convert blob to base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
    });
  };

  // State to store compression info for testing
  const [compressionInfo, setCompressionInfo] = useState({});
  // State to track which files are being compressed
  const [compressingFiles, setCompressingFiles] = useState({});

  // Updated handleFileChange function
  const handleFileChange = async (e) => {
    const { name, files } = e.target;
    
    // Skip file changes for disabled fields
    if (disabledFields.includes(name)) {
      return;
    }
    
    if (files && files[0]) {
      let fileToProcess = files[0];
      const fileSizeInMB = fileToProcess.size / 1024 / 1024;
      console.log(`fileSizeInMB:`, fileSizeInMB);
      
      // Store original file info for testing
      let originalBase64 = null;
      let compressedBase64 = null;
      let wasCompressed = false;
      
      // Check if file exceeds 1MB
      if (fileSizeInMB > 0.75) {
        // If it's an image, try to compress it
        if (isImageFile(fileToProcess)) {
          try {
            // Set compressing state to true for this field
            setCompressingFiles(prev => ({ ...prev, [name]: true }));
            if(SftGlobalData.debug){
              console.log('Setting compressing state to true for:', name);
              console.log('Image exceeds 1MB, attempting compression...');
            }
            
            // Store original for comparison
            originalBase64 = await fileToBase64(files[0]);
            
            const compressedBlob = await compressImage(fileToProcess, 0.75);
            const compressedSizeMB = compressedBlob.size / 1024 / 1024;
            
            if(SftGlobalData.debug){
              console.log(`Compressed size: ${compressedSizeMB.toFixed(2)}MB`);
            }
            
            // Get compressed base64
            compressedBase64 = await blobToBase64(compressedBlob);
            wasCompressed = true;
            
            // Store compression info for testing
            if(SftGlobalData.debug){
              setCompressionInfo(prev => ({
                ...prev,
                [name]: {
                  original: {
                    base64: originalBase64,
                    size: fileSizeInMB,
                    name: files[0].name
                  },
                  compressed: {
                    base64: compressedBase64,
                    size: compressedSizeMB,
                    name: `compressed_${files[0].name}`
                  }
                }
              }));
            }
            
            // Use the compressed blob directly - no need to create a new File
            fileToProcess = compressedBlob;
          } catch (error) {
            console.error('Image compression failed:', error);
            setErrors(prevState => ({
              ...prevState,
              [name]: t('peppol_file_size')
            }));
            // Clear compressing state
            setCompressingFiles(prev => ({ ...prev, [name]: false }));
            return;
          } finally {
            // Always clear compressing state
            setCompressingFiles(prev => ({ ...prev, [name]: false }));
            if(SftGlobalData.debug){
               console.log('Setting compressing state to false for:', name);
            }
          }
        } else {
          // For non-image files (like PDFs), show the error as before
          setErrors(prevState => ({
            ...prevState,
            [name]: t('peppol_file_size')
          }));
          if(SftGlobalData.debug){
            console.log("errors", errors);
          }
          return;
        }
      }

      try {
        // Convert to base64 and store
        let base64Data;
        if (wasCompressed) {
          // If the image was compressed, use the already converted compressed base64
          base64Data = compressedBase64;
        } else {
          // Use existing fileToBase64 function for non-compressed files
          base64Data = await fileToBase64(fileToProcess);
        }
        
        setFormData(prevState => ({
          ...prevState,
          [name]: base64Data
        }));
        
        // Store the file name
        setFileNames(prevState => ({
          ...prevState,
          [name]: files[0].name
        }));
        
        // Clear any errors - properly remove the key
        if (errors[name]) {
          setErrors(prevState => {
            const newErrors = { ...prevState };
            delete newErrors[name];
            return newErrors;
          });
        }
        
        // Clear compression info if file was not compressed and debug is off
        if (!wasCompressed && compressionInfo[name] && !SftGlobalData.debug) {
          setCompressionInfo(prev => {
            const newInfo = { ...prev };
            delete newInfo[name];
            return newInfo;
          });
        }
      } catch (error) {
        console.error(`Error converting ${name} to base64:`, error);
        setErrors(prevState => ({
          ...prevState,
          [name]: `Error processing file: ${error.message}`
        }));
      }
    }
  };

  // Helper function to download base64 as file
  const downloadBase64File = (base64Data, fileName) => {
    const link = document.createElement('a');
    link.href = base64Data;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Component to show compression test results (add this where you want to display the download links)
  const CompressionTestResults = () => {
    if (Object.keys(compressionInfo).length === 0) return null;
    
    return (
      <div style={{ 
        marginTop: '20px', 
        padding: '15px', 
        border: '1px solid #ddd', 
        borderRadius: '5px',
        backgroundColor: '#f5f5f5'
      }}>
        <h3>{t('peppol_compression_title')}</h3>
        {Object.entries(compressionInfo).map(([fieldName, info]) => (
          <div key={fieldName} style={{ marginBottom: '15px' }}>
            <h4>{fieldName}</h4>
            <div style={{ marginBottom: '10px' }}>
              <strong>{t('peppol_original')}:</strong> {info.original.size.toFixed(2)}MB
              <button 
                onClick={() => downloadBase64File(info.original.base64, info.original.name)}
                style={{ marginLeft: '10px', cursor: 'pointer' }}
              >
                Download {t('peppol_original')}
              </button>
            </div>
            <div>
              <strong>{t('peppol_compressed')}:</strong> {info.compressed.size.toFixed(2)}MB (binary) / {info.compressed.base64Size?.toFixed(2) || (info.compressed.size * 1.33).toFixed(2)}MB (base64)
              <span style={{ color: 'green', marginLeft: '10px' }}>
                ({((1 - info.compressed.size / info.original.size) * 100).toFixed(0)}% {t('peppol_savings')})
              </span>
              <button 
                onClick={() => downloadBase64File(info.compressed.base64, info.compressed.name)}
                style={{ marginLeft: '10px', cursor: 'pointer' }}
              >
                Download {t('peppol_compressed')}
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const validate = () => {
    const newErrors = {};

    // Adjust validation based on whether we're editing or creating
    if (!isEditing) {
      // Full validation for new registrations
      if (!formData.corpname) newErrors.corpname = 'Company name is required';
      if (!formData.vat) newErrors.vat = 'VAT number is required';
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!validateEmail(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
      if(!formData.homepage) newErrors.homepage = 'Homepage is required';
      if (!formData.iban) {
        newErrors.iban = 'IBAN is required';
      } else if (!validateIBAN(formData.iban)) {
        newErrors.iban = 'Invalid IBAN format';
      }
      if (!formData.street) newErrors.street = 'Street is required';
      if (!formData.streetNr) newErrors.streetNr = 'Street number is required';
      if (!formData.city) newErrors.city = 'City is required';
      if (!formData.zip) newErrors.zip = 'ZIP code is required';
      if (!formData.country) newErrors.country = 'Country is required';
      if (!formData.phoneNumber) {
        newErrors.phoneNumber = 'Phone number is required';
      } else if (!validatePhone(formData.phoneNumber)) {
        newErrors.phoneNumber = 'Invalid phone number format';
      }
      if (!formData.idDocuments) newErrors.idDocuments = 'ID documents are required';
    } else {
      // Limited validation for editing - only validate fields that aren't disabled
      // and that we've filled in
      if (formData.corpname === '') newErrors.corpname = 'Company name is required';
      if (formData.email && !validateEmail(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
      if (formData.iban && !validateIBAN(formData.iban)) {
        newErrors.iban = 'Invalid IBAN format';
      }
      if (formData.phoneNumber && !validatePhone(formData.phoneNumber)) {
        newErrors.phoneNumber = 'Invalid phone number format';
      }
    }
    
    // Validate consent checkbox (always required)
    if (!consentChecked) {
      newErrors.consent = 'You must confirm that you are authorized to make these changes';
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    console.log(` formData:`, formData);


    // Create a new object with all form data
    const submissionData = {
      ...formData,
      consent: consentChecked,
      countryCorp: formData.country ? countries.find((country) => country.value === formData.country)?.label : ''
    };
    console.log(` submissionData:`, submissionData);

    // Call the onSubmit callback with form data
    if (onSubmit) {
      setLoading(true);
      onSubmit(submissionData);
    } else {
      console.log('Form data submitted:', submissionData);
      alert('Form submitted successfully!');
    }
  };

  // Helper function to check if there are actual errors
  const hasErrors = () => {
    return Object.keys(errors).length > 0;
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* Company Information */}
        <Grid size={12}>
          <Typography variant="h6" gutterBottom>
            {t('companyInformation')}
          </Typography>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            required
            label={t('org_name')}
            name="corpname"
            value={formData.corpname}
            onChange={handleChange}
            error={!!errors.corpname}
            helperText={errors.corpname}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            required
            label={t('vat')}
            name="vat"
            value={formData.vat}
            onChange={handleChange}
            error={!!errors.vat}
            helperText={errors.vat}
            disabled={disabledFields.includes('vat')}
          />
        </Grid>
        
        <Grid size={12}>
          <TextField
            fullWidth
            required
            label={t('iban')}
            name="iban"
            value={formData.iban}
            onChange={handleChange}
            error={!!errors.iban}
            helperText={errors.iban}
          />
        </Grid>
        
        <Grid size={12}>
          <TextField
            fullWidth
            required
            label={t('email')}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
          />
        </Grid>
        
        <Grid size={12}>
          <TextField
            fullWidth
            required
            label={t('homepage')}
            name="homepage"
            type="text"
            value={formData.homepage}
            onChange={handleChange}
            error={!!errors.homepage}
            helperText={errors.homepage}
          />
        </Grid>
        
        {/* Address Information */}
        <Grid size={12}>
          <Typography variant="h6" gutterBottom>
            {t('addressInformation')}
          </Typography>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 8 }}>
          <TextField
            fullWidth
            required
            label={t('street')}
            name="street"
            value={formData.street}
            onChange={handleChange}
            error={!!errors.street}
            helperText={errors.street}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            required
            label={t('streetNr')}
            name="streetNr"
            value={formData.streetNr}
            onChange={handleChange}
            error={!!errors.streetNr}
            helperText={errors.streetNr}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            required
            label={t('city')}
            name="city"
            value={formData.city}
            onChange={handleChange}
            error={!!errors.city}
            helperText={errors.city}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            required
            label={t('zip')}
            name="zip"
            value={formData.zip}
            onChange={handleChange}
            error={!!errors.zip}
            helperText={errors.zip}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth required error={!!errors.country}>
            <InputLabel id="country-label">{t('country')}</InputLabel>
            <Select
              labelId="country-label"
              id="country"
              name='country'
              value={formData.country}
              onChange={handleChange}
              input={<OutlinedInput label="Country" />}
            >
              {countries.map((country) => (
                <MenuItem key={country.value} value={country.value}>
                  {country.label}
                </MenuItem>
              ))}
            </Select>
            {errors.country && <FormHelperText>{errors.country}</FormHelperText>}
          </FormControl>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            required
            label={t('phoneNumber')}
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber}
          />
        </Grid>
        
        {/* Document Uploads - Only show for new registrations */}
        {!isEditing && (
          <>
            <Grid size={12}>
              <Typography variant="h6" gutterBottom>
                {t('documentUploads')}
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box>
                <InputLabel htmlFor="idDocuments" required sx={{ mb: 1 }}>
                {t('idDocument_front')}
                </InputLabel>
                <Box sx={{ position: 'relative' }}>
                  <input
                    accept="image/*,.pdf"
                    id="idDocuments"
                    name="idDocuments"
                    type="file"
                    onChange={handleFileChange}
                    style={{ width: '100%' }}
                    disabled={disabledFields.includes('idDocuments') || compressingFiles.idDocuments}
                  />
                  {compressingFiles.idDocuments && (
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      mt: 1,
                      color: 'primary.main'
                    }}>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        {t('compressing_image') || 'Compressing image...'}
                      </Typography>
                    </Box>
                  )}
                </Box>
                {fileNames.idDocuments && !compressingFiles.idDocuments && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    {t('selected_file')}: {fileNames.idDocuments}
                  </Typography>
                )}
                {errors.idDocuments && (
                  <FormHelperText error>{errors.idDocuments}</FormHelperText>
                )}
                <FormHelperText>
                  {t('idDocument_front_help')}
                </FormHelperText>
              </Box>
            </Grid>
            
            {SftGlobalData.debug && (
              <Grid size={{ xs: 12, sm: 6 }}>
                  <CompressionTestResults />
              </Grid>
            )}
          </>
        )}
        
        {/* Consent Checkbox */}
        <Grid size={12} sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={consentChecked}
                onChange={handleConsentChange}
                name="consent"
                color="primary"
              />
            }
            label={t('peppol_consent')}
            style={{ fontSize: '8px !important' }}
          />
          {errors.consent && (
            <FormHelperText error>{errors.consent}</FormHelperText>
          )}
        </Grid>

        {/* <Grid size={12} sx={{ mt: 2 }}>
          <Typography variant="body2">
            {t('peppol_consent_info_text')}
          </Typography>
        </Grid> */}
        
        {/* Buttons */}
        <Grid size={12} sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                fullWidth
                onClick={onCancel}
              >
                {t('cancel')}
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={!consentChecked || hasErrors()}
              >
                {isEditing ? t('btn_update_peppol') : t('btn_register_peppol')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export default PeppolRegistrationForm;